import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseClient';
import { RefreshCw, Printer, Eye, Package, ExternalLink, ClipboardCopy } from 'lucide-react';

/**
 * Tab component for viewing artist print opportunity signups for an event.
 * Shows summary counts + detail table of who signed up for what.
 */
const EventPrintSignupsTab = ({ eventId, collectionId }) => {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (eventId) loadSignups();
  }, [eventId]);

  const loadSignups = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('event_print_signups')
        .select('*')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const rows = data || [];

      // Fetch artist details for each signup
      const artistUuids = [...new Set(rows.map(s => s.artist_uuid).filter(Boolean))];
      let artistMap = {};
      if (artistUuids.length > 0) {
        const { data: artists } = await supabase
          .from('users')
          .select('uuid, first_name, last_name, nickname, email')
          .in('uuid', artistUuids);

        if (artists) {
          for (const a of artists) {
            artistMap[a.uuid] = a;
          }
        }
      }

      // Attach artist info
      const enriched = rows.map(s => ({
        ...s,
        artist: artistMap[s.artist_uuid] || null,
      }));

      setSignups(enriched);
    } catch (err) {
      console.error('Error loading print signups:', err);
    }
    setLoading(false);
  };

  const scanningCount = signups.filter(s => s.wants_scanning).length;
  const openHouseCount = signups.filter(s => s.wants_open_house).length;
  const printsOnHandCount = signups.filter(s => s.bringing_own_prints).length;
  const printLabSupportCount = signups.filter(
    s => s.wants_scanning || s.wants_open_house,
  ).length;

  const getArtistName = (signup) => {
    const a = signup.artist;
    if (!a) return signup.artist_uuid?.substring(0, 8) + '...';
    if (a.nickname) return a.nickname;
    return [a.first_name, a.last_name].filter(Boolean).join(' ') || a.email;
  };

  const signupUrl = `https://vibrantcast.com/event-print-signup?eventId=${eventId}&eventName=Festival%20of%20Independent%20Artists`;

  const buildReadinessUrl = (signup) => {
    const base = 'https://vibrantcast.com/event-readiness';
    const params = new URLSearchParams();
    if (eventId) params.set('eventId', eventId);
    if (collectionId) params.set('collectionId', collectionId);
    if (signup.artist_uuid) params.set('artistUuid', signup.artist_uuid);
    if (signup.artist?.email) params.set('email', signup.artist.email);
    return `${base}?${params.toString()}`;
  };

  const [copiedId, setCopiedId] = React.useState(null);
  const copyReadinessUrl = (signup) => {
    const url = buildReadinessUrl(signup);
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(signup.id);
      setTimeout(() => setCopiedId(null), 1800);
    });
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
        Loading print signups...
      </div>
    );
  }

  return (
    <div style={{ padding: '0' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
          Print Opportunity Signups ({signups.length})
        </h3>
        <button
          onClick={loadSignups}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', border: '1px solid #ddd', borderRadius: 6,
            background: 'white', cursor: 'pointer', fontSize: 12,
          }}
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Signup link */}
      <div style={{
        padding: '10px 14px', marginBottom: 16,
        background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8,
        fontSize: 12, display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <ExternalLink size={14} style={{ color: '#0284c7', flexShrink: 0 }} />
        <span style={{ color: '#0369a1' }}>
          Artist signup page:{' '}
          <a href={signupUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0284c7', wordBreak: 'break-all' }}>
            {signupUrl}
          </a>
        </span>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        <SummaryCard
          icon={<Printer size={18} />}
          color="#2563eb"
          label="Any PrintLab Support"
          count={printLabSupportCount}
        />
        <SummaryCard
          icon={<Printer size={18} />}
          color="#8b5cf6"
          label="Needs Scanning"
          count={scanningCount}
        />
        <SummaryCard
          icon={<Eye size={18} />}
          color="#f59e0b"
          label="Needs Open House"
          count={openHouseCount}
        />
        <SummaryCard
          icon={<Package size={18} />}
          color="#10b981"
          label="Has Prints On Hand"
          count={printsOnHandCount}
        />
      </div>

      {/* Table */}
      {signups.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#888', background: '#fafafa', borderRadius: 8 }}>
          No signups yet. Share the link with your artists!
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Artist</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Email</th>
                <th style={{ padding: '8px 8px', fontWeight: 600, textAlign: 'center' }}>Scanning</th>
                <th style={{ padding: '8px 8px', fontWeight: 600, textAlign: 'center' }}>Open House</th>
                <th style={{ padding: '8px 8px', fontWeight: 600, textAlign: 'center' }}>Prints On Hand</th>
                <th style={{ padding: '8px 12px', fontWeight: 600, textAlign: 'center' }}>PrintLab Support</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Notes</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Signed Up</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Readiness</th>
              </tr>
            </thead>
            <tbody>
              {signups.map((signup) => (
                <tr key={signup.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 500 }}>
                    {getArtistName(signup)}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#6b7280', fontSize: 12 }}>
                    {signup.artist?.email || '—'}
                  </td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                    {signup.wants_scanning ? '✅' : '—'}
                  </td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                    {signup.wants_open_house ? '✅' : '—'}
                  </td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                    {signup.bringing_own_prints ? '✅' : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 12 }}>
                    {signup.wants_scanning && signup.wants_open_house
                      ? 'Scan + House'
                      : signup.wants_scanning
                          ? 'Scan'
                          : signup.wants_open_house
                              ? 'Open House'
                              : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#6b7280', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {signup.notes || '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#9ca3af', fontSize: 11 }}>
                    {new Date(signup.created_at).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                      timeZone: 'America/Chicago'
                    })}
                  </td>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => copyReadinessUrl(signup)}
                        title="Copy readiness URL"
                        style={{
                          display: 'flex', alignItems: 'center', gap: 4,
                          padding: '4px 8px', border: '1px solid #d1d5db',
                          borderRadius: 5, background: copiedId === signup.id ? '#d1fae5' : 'white',
                          cursor: 'pointer', fontSize: 11, color: copiedId === signup.id ? '#065f46' : '#374151',
                        }}
                      >
                        <ClipboardCopy size={12} />
                        {copiedId === signup.id ? 'Copied!' : 'Copy URL'}
                      </button>
                      <a
                        href={buildReadinessUrl(signup)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open readiness page"
                        style={{
                          display: 'flex', alignItems: 'center', gap: 4,
                          padding: '4px 8px', border: '1px solid #bfdbfe',
                          borderRadius: 5, background: '#eff6ff',
                          fontSize: 11, color: '#1d4ed8', textDecoration: 'none',
                        }}
                      >
                        <ExternalLink size={12} /> Open
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ icon, color, label, count }) => (
  <div style={{
    padding: '14px 16px', background: 'white', borderRadius: 10,
    border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 12,
  }}>
    <div style={{
      width: 36, height: 36, borderRadius: 8,
      background: `${color}18`, display: 'flex', alignItems: 'center',
      justifyContent: 'center', color,
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{count}</div>
      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{label}</div>
    </div>
  </div>
);

export default EventPrintSignupsTab;
